import routes from '../routes/routes';
import { getActiveRoute, getActivePathname } from '../routes/url-parser';
import HomePresenter from './home/home-presenter';
import NearbyPresenter from './nearby/nearby-presenter';
import AuthPresenter from './auth/auth-presenter';
import ProfilePresenter from './profile/profile-presenter';
import BookmarksPresenter from './bookmarks/bookmarks-presenter';
import NotificationsPresenter from './notifications/notifications-presenter';
import PrivacyPresenter from './privacy/privacy-presenter';
import CookiesPresenter from './cookies/cookies-presenter';
import PostPresenter from './post/post-presenter';
import NotFoundPresenter from './not-found/not-found-presenter';
import SidebarPresenter from '../common/sidebar-presenter';
import ThemeService from '../services/theme-service';
import AuthService from '../services/auth-service';
import BookmarkService from '../services/bookmark-service';
import NotificationService from '../services/notification-service';
import * as UIService from '../services/ui-service';
import PostModel from './post/post-model';
import StorySource from '../data/story-source';
import CONFIG from '../config/app-config';
import { urlBase64ToUint8Array } from '../utils/helpers';
import LocalStorage from '../data/local-storage';
import TransitionHelper from '../utils/transition';

class App {
  constructor() {
    this.currentPage = 'home';
    this.presenters = {};
    this.currentPostId = null;
    this.sidebarPresenter = null;
    this.init();
  }

  async init() {
    AuthService.load();
    ThemeService.load();
    await BookmarkService.load();
    await NotificationService.load();
    this.checkInitialRoute();
    this.updateNavigationState(this.currentPage);
    this.updatePageTitle(this.currentPage);
    this.setupGlobalEventListeners();
    this.setupHistoryManagement();
    ThemeService.apply();
    UIService.initTooltips();
    BookmarkService.updateBubble();
    NotificationService.updateBubble();
    this.updateNotificationButtonState();
    this.setupDistanceStorageListener();
    this.setupMapLayerStorageListener();
    this.initDistanceDisplay();
    const sidebarInterface = this.createAppInterface();
    this.sidebarPresenter = new SidebarPresenter(sidebarInterface);
    await this.sidebarPresenter.init();
    await this.renderCurrentPage();
  }

  setupGlobalEventListeners() {
    document.querySelectorAll('.nav-link').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const page = e.currentTarget.dataset.page;
        if (page === this.currentPage) {
          if (page === 'home') {
            this.refreshCurrentPage();
          }
          return;
        }
        this.navigateTo(page);
      });
    });

    const homeLink = document.querySelector('#home-link');
    if (homeLink) {
      homeLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.currentPage === 'home') {
          this.refreshCurrentPage();
        } else {
          this.navigateTo('home');
        }
      });
    }

    document.querySelectorAll('.theme-selector, .setting-option[data-theme]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const theme = e.currentTarget.dataset.theme;
        if (theme) this.setTheme(theme);
      });
    });

    document.querySelectorAll('.font-selector, .setting-option[data-font]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const font = e.currentTarget.dataset.font;
        if (font) this.setFont(font);
      });
    });

    const bookmarkTick = document.querySelector('#bookmark-tick');
    if (bookmarkTick) {
      bookmarkTick.addEventListener('click', (e) => {
        e.stopPropagation();
        UIService.togglePopup('dialog-bookmark');
      });
    }

    const notificationTick = document.querySelector('#notification-tick');
    if (notificationTick) {
      notificationTick.addEventListener('click', (e) => {
        e.stopPropagation();
        UIService.togglePopup('dialog-notification');
      });
    }

    const headerNotifBtn = document.querySelector('#enable-notifications');
    if (headerNotifBtn) {
      headerNotifBtn.addEventListener('click', () => {
        if (!AuthService.isAuthenticated) {
          this.showToast('Masuk terlebih dahulu untuk mengaktifkan notifikasi', 'warning');
          return;
        }
        if (AuthService.getPushSubscription()) {
          this.unsubscribeFromNotifications();
        } else {
          this.enableNotifications();
        }
      });
    }

    const settingsBtn = document.querySelector('#settings-btn');
    if (settingsBtn) {
      settingsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        UIService.togglePopup('app-setting');
      });
    }

    const distanceFilter = document.querySelector('#distance-filter');
    if (distanceFilter) {
      distanceFilter.addEventListener('click', (e) => {
        e.stopPropagation();
        UIService.togglePopup('distance-dialog');
      });
    }

    const rangeLocation = document.querySelector('#range-location');
    if (rangeLocation) {
      rangeLocation.addEventListener('input', (e) => {
        const display = document.querySelector('#range-display');
        if (display) display.textContent = e.target.value;
      });
    }

    document.querySelectorAll('.save-jarak').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (rangeLocation) {
          const val = parseInt(rangeLocation.value);
          const display = document.querySelector('#distance-display');
          if (display) display.textContent = val;
          if (AuthService.isAuthenticated && AuthService.user.id) {
            LocalStorage.setUserSpecificItem(
              'heliaedu_user_distance',
              AuthService.user.id,
              val.toString()
            );
          } else {
            LocalStorage.setItem('heliaedu_guest_distance', val.toString());
          }
          if (this.presenters.nearby && this.presenters.nearby.updateDistance) {
            this.presenters.nearby.updateDistance(val);
          }
          if (this.sidebarPresenter) {
            this.sidebarPresenter.refresh();
          }
        }
        UIService.hidePopup('distance-dialog');
        this.showToast('Jarak diatur', 'success');
      });
    });

    document.querySelectorAll('.cancel-jarak').forEach((btn) => {
      btn.addEventListener('click', () => {
        UIService.hidePopup('distance-dialog');
      });
    });

    document.addEventListener('click', (e) => {
      if (
        !e.target.closest('.bookmark-wrapper-tick') &&
        !e.target.closest('.notification-wrapper-tick') &&
        !e.target.closest('.app-setting') &&
        !e.target.closest('#settings-btn') &&
        !e.target.closest('#distance-dialog') &&
        !e.target.closest('#distance-filter')
      ) {
        UIService.hideAllPopups();
      }
    });

    const copyBtn = document.querySelector('#copy-link');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        UIService.copyShareLink();
      });
    }

    const waBtn = document.querySelector('#share-whatsapp');
    if (waBtn) waBtn.addEventListener('click', () => UIService.shareToSocial('whatsapp'));
    const fbBtn = document.querySelector('#share-facebook');
    if (fbBtn) fbBtn.addEventListener('click', () => UIService.shareToSocial('facebook'));
    const twBtn = document.querySelector('#share-twitter');
    if (twBtn) twBtn.addEventListener('click', () => UIService.shareToSocial('twitter'));

    const closeLightbox = document.querySelector('#close-lightbox');
    if (closeLightbox) {
      closeLightbox.addEventListener('click', () => UIService.hideLightbox());
    }

    document.querySelectorAll('.close-dialog').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const dlg = e.currentTarget.closest('.dialog');
        if (dlg) this.hideDialog(dlg.id);
      });
    });

    document.querySelectorAll('.bg-drop').forEach((bg) => {
      bg.addEventListener('click', (e) => {
        if (e.target.classList.contains('bg-drop')) {
          const dlg = e.currentTarget.closest('.dialog');
          if (dlg) this.hideDialog(dlg.id);
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const dlg = document.querySelector('.dialog.show');
        if (dlg) {
          this.hideDialog(dlg.id);
        } else if (document.querySelector('#lightbox').classList.contains('active')) {
          UIService.hideLightbox();
        } else {
          UIService.hideAllPopups();
        }
      }
    });

    window.addEventListener('scroll', () => {
      document.body.classList.toggle('on-scrolled', window.scrollY > 5);
    });
  }

  setupHistoryManagement() {
    window.addEventListener('hashchange', () => this.handleRouteChange());
    window.addEventListener('popstate', () => {
      const dlg = document.querySelector('.dialog.show');
      if (dlg) {
        this.hideDialog(dlg.id, true);
        return;
      }
      if (document.querySelector('#lightbox').classList.contains('active')) {
        UIService.hideLightbox();
        return;
      }
      this.handleRouteChange();
    });
  }


  checkInitialRoute() {
    const pathname = getActivePathname();
    if (pathname.startsWith('/share/')) {
      this.currentPage = 'post';
      this.currentPostId = pathname.replace('/share/', '');
    } else if (pathname.startsWith('/post/')) {
      this.currentPage = 'post';
      this.currentPostId = pathname.replace('/post/', '');
    } else {
      const route = getActiveRoute();
      this.currentPage = route === '/' ? 'home' : route.substring(1);
      this.currentPostId = null;
    }
  }

  initDistanceDisplay() {
    const display = document.getElementById('distance-display');
    const range = document.getElementById('range-location');
    let val;
    if (AuthService.isAuthenticated && AuthService.user.id) {
      val = LocalStorage.getUserSpecificItem(
        'heliaedu_user_distance',
        AuthService.user.id
      );
      if (!val) {
        LocalStorage.setUserSpecificItem('heliaedu_user_distance', AuthService.user.id, '500');
      }
    } else {
      val = LocalStorage.getItem('heliaedu_guest_distance');
      if (!val) LocalStorage.setItem('heliaedu_guest_distance', '500');
    }
    const num = parseInt(val || '500');
    if (display) display.textContent = num;
    if (range) range.value = num;
  }

  setupDistanceStorageListener() {
    window.addEventListener('storage', (e) => {
      if (!e.key) return;

      const userKey =
        AuthService.user && AuthService.user.id
          ? LocalStorage.getUserSpecificKey('heliaedu_user_distance', AuthService.user.id)
          : null;

      if (e.key === userKey || (!userKey && e.key === 'heliaedu_guest_distance')) {
        const newVal = parseInt(e.newValue || '0');
        if (!isNaN(newVal)) {
          this.handleExternalDistanceChange(newVal);
        }
      }
    });
  }

  setupMapLayerStorageListener() {
    window.addEventListener('storage', (e) => {
      if (!e.key) return;

      const userKey =
        AuthService.user && AuthService.user.id
          ? LocalStorage.getUserSpecificKey('heliaedu_user_map_layer', AuthService.user.id)
          : null;

      if (e.key === userKey || (!userKey && e.key === 'heliaedu_guest_map_layer')) {
        const newLayer = e.newValue || 'streets';
        this.handleExternalMapLayerChange(newLayer);
      }
    });
  }

  handleExternalDistanceChange(newDistance) {
    const display = document.getElementById('distance-display');
    const range = document.getElementById('range-location');
    if (display) display.textContent = newDistance;
    if (range) range.value = newDistance;

    if (this.presenters.nearby && this.presenters.nearby.updateDistance) {
      this.presenters.nearby.updateDistance(newDistance);
    }

    if (this.sidebarPresenter) {
      this.sidebarPresenter.refresh();
    }
  }

  handleExternalMapLayerChange(layerType) {
    if (
      this.presenters.home &&
      typeof this.presenters.home.applyExternalMapLayer === 'function'
    ) {
      this.presenters.home.applyExternalMapLayer(layerType);
    }

    if (
      this.presenters.nearby &&
      typeof this.presenters.nearby.applyExternalMapLayer === 'function'
    ) {
      this.presenters.nearby.applyExternalMapLayer(layerType);
    }
  }

  handleRouteChange() {
    const pathname = getActivePathname();
    if (pathname.startsWith('/share/')) {
      const postId = pathname.replace('/share/', '');
      this.navigateToPost(postId);
      return;
    }
    if (pathname.startsWith('/post/')) {
      const postId = pathname.replace('/post/', '');
      if (!(this.currentPage === 'post' && this.currentPostId === postId)) {
        this.navigateToPost(postId);
      }
      return;
    }
    const route = getActiveRoute();
    const page = route === '/' ? 'home' : route.substring(1);
    if (this.currentPage !== page) {
      this.navigateTo(page);
    }
  }

  async navigateTo(page) {
    if (this.currentPage === page) return;
    const prevPage = this.currentPage;
    if (this.presenters[prevPage] && this.presenters[prevPage].cleanup) {
      this.presenters[prevPage].cleanup();
      this.presenters[prevPage] = null;
    }
    this.currentPage = page;
    if (page !== 'post') this.currentPostId = null;
    this.updateNavigationState(page);
    this.updatePageTitle(page);
    if (page === 'home') {
      window.location.hash = '/';
    } else if (page !== '404') {
      window.location.hash = `/${page}`;
    }
    if (window.scrollY > 0) {
      this.scrollToTop();
    }
    await this.renderCurrentPage();
  }

  async navigateToPost(postId) {
    if (this.currentPage === 'post' && this.currentPostId === postId) return;

    const prevPage = this.currentPage;
    if (this.presenters[prevPage] && this.presenters[prevPage].cleanup) {
      this.presenters[prevPage].cleanup();
      this.presenters[prevPage] = null;
    }
    this.currentPage = 'post';
    this.updatePageTitle('post');
    this.currentPostId = postId;
    if (window.location.hash !== `#/post/${postId}`) {
      window.location.hash = `/post/${postId}`;
    }
    if (window.scrollY > 0) {
      this.scrollToTop();
    }
    await this.renderCurrentPage();
  }

  async renderCurrentPage() {
    const container = document.querySelector('#main-content-area');
    if (!container) return;
    const routeMap = {
      home: '/',
      nearby: '/nearby',
      notifications: '/notifications',
      bookmarks: '/bookmarks',
      profile: '/profile',
      auth: '/auth',
      privacy: '/privacy',
      cookies: '/cookies',
      post: '/post/:id',
      404: '/404',
    };
    const route = routeMap[this.currentPage];
    const page = routes[route];
    if (page) {
      container.innerHTML = await page.render();
      await page.afterRender();
      const extra = this.currentPage === 'post' ? this.currentPostId : undefined;
      await this.initializePresenter(this.currentPage, extra);
    } else {
      this.currentPage = '404';
      await this.renderCurrentPage();
      return;
    }
    AuthService.updateAuthUI();
    UIService.reinitTooltips();
    if (this.sidebarPresenter) {
      await this.sidebarPresenter.refresh();
    }
  }

  async initializePresenter(page, data) {
    if (this.presenters[page] && this.presenters[page].cleanup) {
      this.presenters[page].cleanup();
    }
    this.presenters[page] = null;
    const iface = this.createAppInterface();
    switch (page) {
      case 'home':
        this.presenters.home = new HomePresenter(null, routes['/'], iface);
        await this.presenters.home.init();
        break;
      case 'nearby':
        this.presenters.nearby = new NearbyPresenter(null, routes['/nearby'], iface);
        await this.presenters.nearby.init();
        break;
      case 'auth':
        this.presenters.auth = new AuthPresenter(null, routes['/auth'], iface);
        await this.presenters.auth.init();
        break;
      case 'profile':
        this.presenters.profile = new ProfilePresenter(null, routes['/profile'], iface);
        await this.presenters.profile.init();
        break;
      case 'bookmarks':
        this.presenters.bookmarks = new BookmarksPresenter(null, routes['/bookmarks'], iface);
        await this.presenters.bookmarks.init();
        break;
      case 'notifications':
        this.presenters.notifications = new NotificationsPresenter(
          null,
          routes['/notifications'],
          iface
        );
        await this.presenters.notifications.init();
        break;
      case 'privacy':
        this.presenters.privacy = new PrivacyPresenter(routes['/privacy'], iface);
        await this.presenters.privacy.init();
        break;
      case 'cookies':
        this.presenters.cookies = new CookiesPresenter(routes['/cookies'], iface);
        await this.presenters.cookies.init();
        break;
      case 'post':
        this.presenters.post = new PostPresenter(new PostModel(), routes['/post/:id'], iface);
        await this.presenters.post.init(data);
        break;
      case '404':
        this.presenters.notFound = new NotFoundPresenter(null, routes['/404'], iface);
        await this.presenters.notFound.init();
        break;
    }
  }

  createAppInterface() {
    return {
      showToast: (message, type, duration) => this.showToast(message, type, duration),
      showDialog: (id) => this.showDialog(id),
      hideDialog: (id) => this.hideDialog(id),
      showLightbox: (src) => this.showLightbox(src),
      showConfirmDialog: (msg, onConfirm) => this.showConfirmDialog(msg, onConfirm),
      navigateTo: (page) => this.navigateTo(page),
      navigateToPost: (id) => this.navigateToPost(id),
      navigateBack: () => this.navigateBack(),
      getCurrentPage: () => this.currentPage,
      isAuthenticated: () => AuthService.isAuthenticated,
      getUser: () => AuthService.user,
      setAuthState: (auth, user, token) => this.setAuthState(auth, user, token),
      setTheme: (theme) => this.setTheme(theme),
      setFont: (font) => this.setFont(font),
      setShareUrl: (url) => this.setShareUrl(url),
      updatePageTitle: (title) => this.updatePageTitle(title),
      getCurrentUrl: () => this.getCurrentUrl(),
      getBookmarks: () => BookmarkService.getBookmarks(),
      setBookmarks: (b) => BookmarkService.setBookmarks(b),
      updateBookmarkBubble: () => BookmarkService.updateBubble(),
      getNotifications: () => NotificationService.getNotifications(),
      setNotifications: (n) => NotificationService.setNotifications(n),
      updateNotificationBubble: () => NotificationService.updateBubble(),
      removeNotificationFromApp: (id) => NotificationService.removeNotification(id),
      enableNotifications: () => this.enableNotifications(),
      unsubscribeFromNotifications: () => this.unsubscribeFromNotifications(),
      switchDialog: (fromId, toId, callback) => UIService.switchDialog(fromId, toId, callback),
      reinitTooltips: () => UIService.reinitTooltips(),
      toggleDistanceDialog: () => this.toggleDistanceDialog(),
      updateDistance: (d) => {
        if (this.presenters.nearby && this.presenters.nearby.updateDistance) {
          this.presenters.nearby.updateDistance(d);
        }
        if (this.sidebarPresenter) {
          this.sidebarPresenter.refresh();
        }
      },
    };
  }

  updateNavigationState(currentPage) {
    document.querySelectorAll('.nav-link').forEach((link) => link.classList.remove('active'));
    if (currentPage !== 'post' && currentPage !== '404') {
      const activeLink = document.querySelector(`.nav-link[data-page="${currentPage}"]`);
      if (activeLink) activeLink.classList.add('active');
    }
  }

  updatePageTitle(page) {
    const titles = {
      home: 'Beranda',
      nearby: 'Disekitar',
      notifications: 'Notifikasi',
      bookmarks: 'Tersimpan',
      profile: 'Profil',
      auth: 'Masuk',
      post: 'Postingan',
      privacy: 'Kebijakan Privasi',
      cookies: 'Kebijakan Kuki',
      404: 'Halaman Tidak Ditemukan',
    };
    const title = titles[page] || 'Halaman';
    const titleEl = document.querySelector('#page-title');
    if (titleEl) titleEl.textContent = title;
    document.title = `${title} - HeliaEDU`;
  }

  updateTheme(theme) {
    ThemeService.updateTheme(theme);
  }

  updateFont(font) {
    ThemeService.updateFont(font);
  }
  showToast(message, type = 'success', duration = 3000) {
    UIService.showToast(message, type, duration);
  }

  setTheme(theme) {
    ThemeService.setTheme(theme);
  }

  setFont(font) {
    ThemeService.setFont(font);
  }

  setAuthState(isAuth, user = {}, token = null) {
    AuthService.setAuthState(isAuth, user, token);
    this.updateNotificationButtonState();
    if (this.sidebarPresenter) {
      this.sidebarPresenter.refresh();
    }
    UIService.reinitTooltips();
  }

  showDialog(id) {
    UIService.showDialog(id);
    if (
      id === 'camera-canvas-dialog' &&
      this.presenters.home &&
      typeof this.presenters.home.isCameraDialogOpen !== 'undefined'
    ) {
      this.presenters.home.isCameraDialogOpen = true;
    }
  }

  hideDialog(id, fromPopstate = false) {
    if (
      id === 'camera-canvas-dialog' &&
      this.presenters.home &&
      this.presenters.home.view &&
      typeof this.presenters.home.view.stopCameraStream === 'function'
    ) {
      this.presenters.home.view.stopCameraStream();
      if (typeof this.presenters.home.isCanvasCameraInitialized !== 'undefined') {
        this.presenters.home.isCanvasCameraInitialized = false;
      }
      if (typeof this.presenters.home.isCameraDialogOpen !== 'undefined') {
        this.presenters.home.isCameraDialogOpen = false;
      }
    }

    UIService.hideDialog(id, fromPopstate);
  }

  showLightbox(src) {
    UIService.showLightbox(src);
  }

  hideLightbox() {
    UIService.hideLightbox();
  }

  toggleDistanceDialog() {
    UIService.togglePopup('distance-dialog');
  }

  // bookmark and notification helpers moved to dedicated services

  setShareUrl(url) {
    UIService.setShareUrl(url);
  }

  getCurrentUrl() {
    return window.location.origin + window.location.pathname;
  }

  navigateBack() {
    if (window.history && window.history.length > 1) {
      window.history.back();
    } else {
      this.navigateTo('home');
    }
  }

  showConfirmDialog(message, onConfirm) {
    UIService.showConfirmDialog(message, onConfirm);
  }

  updateNotificationButtonState() {
    const isSubscribed = !!AuthService.getPushSubscription();
    const headerBtn = document.querySelector('#enable-notifications');
    if (headerBtn) {
      if (isSubscribed) {
        headerBtn.innerHTML =
          '<span class="material-symbols-rounded" aria-hidden="true">notifications_off</span><p>Nonaktifkan notifikasi</p>';
        headerBtn.setAttribute('data-text_tip', 'Nonaktifkan notifikasi push');
      } else {
        headerBtn.innerHTML =
          '<span class="material-symbols-rounded" aria-hidden="true">notifications_active</span><p>Aktifkan notifikasi</p>';
        headerBtn.setAttribute('data-text_tip', 'Aktifkan notifikasi push');
      }
      UIService.reinitTooltips();
    }
  }

  setNotificationButtonsLoading(isLoading) {
    const buttons = document.querySelectorAll(
      '#enable-notifications, #enable-notifications-page, #enable-notifications-profile'
    );
    buttons.forEach((btn) => {
      if (isLoading) {
        btn.disabled = true;
        btn.innerHTML = '<span class="material-symbols-rounded spin">progress_activity</span>';
      } else {
        btn.disabled = false;
      }
    });
  }



  scrollToTop() {
    window.scrollTo({ top: 0 });
  }

  async refreshCurrentPage() {
    if (window.scrollY > 0) {
      this.scrollToTop();
    }
    await TransitionHelper.fadeTransition(async () => {
      if (
        this.presenters[this.currentPage] &&
        typeof this.presenters[this.currentPage].refresh === 'function'
      ) {
        await this.presenters[this.currentPage].refresh();
      } else {
        await this.renderCurrentPage();
      }
    });
  }


  async enableNotifications() {
    if (!AuthService.isAuthenticated) {
      this.showToast('Masuk terlebih dahulu untuk mengaktifkan notifikasi', 'warning');
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      this.showToast('Notifikasi push tidak didukung browser ini', 'error');
      return;
    }

    try {
      this.setNotificationButtonsLoading(true);
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this.showToast('Izin notifikasi ditolak', 'error');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(CONFIG.VAPID_KEY),
      });

      const p256dh = btoa(
        String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))
      );
      const auth = btoa(
        String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))
      );
      const data = { endpoint: subscription.endpoint, keys: { p256dh, auth } };

      await StorySource.subscribeToNotifications(data);

      AuthService.setPushSubscription(data);
      NotificationService.addNotification({
        id: 'notif_' + Date.now(),
        title: 'Notifikasi Aktif',
        message: 'Anda akan menerima notifikasi untuk aktivitas terbaru',
        timestamp: new Date().toISOString(),
      });

      if (this.presenters.notifications && this.presenters.notifications.updateNotificationButton) {
        this.presenters.notifications.updateNotificationButton(true);
      }
      if (this.presenters.profile && this.presenters.profile.updateNotificationButton) {
        this.presenters.profile.updateNotificationButton(true);
      }

      this.updateNotificationButtonState();

      this.showToast('Notifikasi berhasil diaktifkan', 'success');
    } catch (error) {
      this.showToast('Gagal mengaktifkan notifikasi', 'error');
    } finally {
      this.setNotificationButtonsLoading(false);
    }
  }

  async unsubscribeFromNotifications() {
    const sub = AuthService.getPushSubscription();
    if (!sub) {
      this.showToast('Notifikasi tidak aktif', 'warning');
      return;
    }

    try {
      this.setNotificationButtonsLoading(true);
      await StorySource.unsubscribeFromNotifications(sub.endpoint);
    } catch (e) {
      // ignore API errors
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const s = await registration.pushManager.getSubscription();
      if (s) await s.unsubscribe();
    } catch (e) {
      // ignore
    }

    AuthService.setPushSubscription(null);

    if (this.presenters.notifications && this.presenters.notifications.updateNotificationButton) {
      this.presenters.notifications.updateNotificationButton(false);
    }
    if (this.presenters.profile && this.presenters.profile.updateNotificationButton) {
      this.presenters.profile.updateNotificationButton(false);
    }

    this.updateNotificationButtonState();

    this.showToast('Notifikasi berhasil dinonaktifkan', 'success');
    this.setNotificationButtonsLoading(false);
  }

  cleanup() {
    Object.values(this.presenters).forEach((p) => {
      if (p && p.cleanup) p.cleanup();
    });
  }
}

export default App;
